import { useEffect, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { authHeaders, fn, supabase } from '../auth/supabase'

interface RosterName {
  id: string
  display_name: string
}

/**
 * Signed in, but we do not yet know who this is.
 *
 * The roster is read straight from Postgres rather than through the snapshot,
 * because at this moment the user is authenticated but not a member and the
 * snapshot is for members. A dedicated policy allows exactly this one read --
 * current-season player names, nothing else.
 */
export function ClaimName() {
  const { email, refreshMember, signOut } = useAuth()
  const [names, setNames] = useState<RosterName[] | null>(null)
  const [picked, setPicked] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!supabase) return
    let cancelled = false
    supabase
      .from('players')
      .select('id, display_name')
      .order('display_name')
      .then(({ data, error: err }) => {
        if (cancelled) return
        if (err) setError(`Could not load the roster: ${err.message}`)
        else setNames((data ?? []) as RosterName[])
      })
    return () => {
      cancelled = true
    }
  }, [])

  const claim = async () => {
    if (!picked) return
    const url = fn('claim')
    if (!url) return
    setBusy(true)
    setError(null)
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { ...(await authHeaders()), 'content-type': 'application/json' },
        body: JSON.stringify({ playerId: picked }),
      })
      const body = (await res.json().catch(() => ({}))) as { error?: string }
      if (!res.ok) {
        setError(body.error ?? 'Could not record that. Try again.')
        return
      }
      await refreshMember()
    } catch {
      setError('No connection. Try again when you are back online.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="landing">
      <div className="landing__card landing__card--wide">
        <div className="landing__label">Which one are you?</div>
        <p className="muted small">
          Signed in as {email}. Pick your name so your stats are yours. The admin confirms it before you
          get in.
        </p>

        {error && <div className="manage__note manage__note--error">{error}</div>}

        {names === null ? (
          <p className="muted">Loading the roster...</p>
        ) : names.length === 0 ? (
          <p className="muted">No roster yet. It appears after the first sync.</p>
        ) : (
          <div className="chip-grid chip-grid--players">
            {names.map((n) => (
              <button
                key={n.id}
                type="button"
                className={'chip chip--player' + (picked === n.id ? ' chip--on' : '')}
                onClick={() => setPicked(n.id)}
              >
                {n.display_name}
              </button>
            ))}
          </div>
        )}

        <button
          type="button"
          className="btn btn--primary btn--start"
          disabled={!picked || busy}
          onClick={() => void claim()}
        >
          {busy ? 'Sending...' : picked ? 'That is me' : 'Pick your name'}
        </button>

        <button type="button" className="btn" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </div>
  )
}

/** Claimed, waiting on the admin. */
export function PendingApproval() {
  const { member, email, refreshMember, signOut } = useAuth()
  const [checking, setChecking] = useState(false)
  return (
    <div className="landing">
      <div className="landing__card">
        <div className="landing__label">Almost there</div>
        <div className="landing__opponent">{member?.display_name ?? 'Claim sent'}</div>
        <p>
          Your claim is with the admin. Once it is approved you will see the roster, the live tally and the
          season stats.
        </p>
        <p className="muted small">Signed in as {email}.</p>
        <button
          type="button"
          className="btn btn--primary"
          disabled={checking}
          onClick={() => {
            setChecking(true)
            void refreshMember().finally(() => setChecking(false))
          }}
        >
          {checking ? 'Checking...' : 'Check again'}
        </button>
        <button type="button" className="btn" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </div>
  )
}

/** Access removed. Deliberately says little. */
export function Revoked() {
  const { signOut } = useAuth()
  return (
    <div className="landing">
      <div className="landing__card">
        <div className="landing__label">No access</div>
        <p>This account does not have access to the team's stats. Have a word with the admin.</p>
        <button type="button" className="btn" onClick={() => void signOut()}>
          Sign out
        </button>
      </div>
    </div>
  )
}
